import * as React from 'react';
import { cn } from '../lib/utils';

export interface MasterDetailLayoutProps {
  /** Master pane — typically a list. */
  master: React.ReactNode;
  /** Detail pane — selected record. */
  detail: React.ReactNode;
  /** Optional inspector pane on the right. */
  inspector?: React.ReactNode;
  /** Initial widths in % (master, detail). Inspector takes the remainder. */
  defaultWidths?: { master: number; detail: number };
  /** Persistence key for column widths. */
  storageKey?: string;
  /** Called when keyboard navigation moves selection ('next' or 'prev'). */
  onKeyboardSelect?: (direction: 'next' | 'prev') => void;
  className?: string;
}

/**
 * Tyndo MasterDetailLayout — three-pane resizable canvas for record-heavy
 * modules (Quality NCs, Maintenance work orders, Alarms, CRM contacts,
 * Procurement orders, Workflow inboxes, Batches).
 *
 * Layout: [Master | Detail | Inspector?]
 *   - Drag the splitter between panes to resize.
 *   - Widths persist to localStorage via `storageKey`.
 *   - j/k cycles selection (host wires `onKeyboardSelect`).
 *
 * Responsive: at >= md (768px) the panes render as the resizable horizontal
 * row described above. Below 768px the panes stack into a single scrollable
 * column (master, then detail, then inspector) at full width, so on tablet /
 * phone the layout flows vertically instead of crushing three panes side by
 * side. Drag-to-resize and the percentage widths apply only to the desktop row.
 */
export function MasterDetailLayout({
  master,
  detail,
  inspector,
  defaultWidths = { master: 28, detail: 47 },
  storageKey,
  onKeyboardSelect,
  className,
}: MasterDetailLayoutProps) {
  const [widths, setWidths] = React.useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(`tyndo.md.${storageKey}`);
        if (stored) return JSON.parse(stored) as { master: number; detail: number };
      } catch {
        /* ignore */
      }
    }
    return defaultWidths;
  });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<{ which: 'master' | 'detail' } | null>(null);

  // Collapse the three-pane row to a single stacked column below the md
  // breakpoint (768px). SSR-safe: assume desktop on first render, then sync to
  // the real viewport on mount and on every breakpoint crossing.
  const [isDesktop, setIsDesktop] = React.useState(true);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  React.useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(`tyndo.md.${storageKey}`, JSON.stringify(widths));
    } catch {
      /* ignore */
    }
  }, [widths, storageKey]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      if (dragRef.current.which === 'master') {
        const next = Math.min(60, Math.max(15, pct));
        setWidths((w) => ({ ...w, master: next }));
      } else {
        const masterPct = widths.master;
        const next = Math.min(85 - masterPct, Math.max(20, pct - masterPct));
        setWidths((w) => ({ ...w, detail: next }));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [widths]);

  React.useEffect(() => {
    if (!onKeyboardSelect) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        onKeyboardSelect('next');
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        onKeyboardSelect('prev');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onKeyboardSelect]);

  const startDrag = (which: 'master' | 'detail') => () => {
    dragRef.current = { which };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const detailPct = widths.detail;
  const inspectorPct = inspector ? Math.max(0, 100 - widths.master - widths.detail) : 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-full w-full bg-surface-base',
        isDesktop ? 'flex overflow-hidden' : 'flex flex-col overflow-y-auto',
        className,
      )}
    >
      {/* PANE SCROLLING IS LOAD-BEARING, NOT COSMETIC.
        *
        * These panes were `h-full overflow-hidden`. On a tall monitor that is
        * invisible; on the 1280x720 industrial panel this product is deployed
        * on it silently amputates the pane. Measured on the production
        * work-order list at 1280x720: the master aside was 454px tall around
        * 900px of content, and the clipped 446px contained the list's own
        * search box and filter chips — with no scrollbar, no document scroll
        * and no scrollable ancestor, so nothing an operator can do reaches
        * them.
        *
        * `min-h-0` is required alongside: a flex item's default
        * `min-height:auto` refuses to shrink below its content, so without it
        * the pane overflows its parent instead of scrolling inside itself.
        * Horizontal stays hidden — panes are width-constrained by the splitter
        * and a sideways scrollbar there is a real defect. */}
      <aside
        style={isDesktop ? { width: `${widths.master}%` } : undefined}
        className={cn(
          'flex min-w-0 flex-col bg-surface-raised',
          isDesktop
            ? 'h-full min-h-0 overflow-x-hidden overflow-y-auto border-r border-border'
            : 'w-full border-b border-border',
        )}
      >
        {master}
      </aside>
      {isDesktop && <Splitter onPointerDown={startDrag('master')} />}
      <section
        style={
          isDesktop
            ? {
                width: inspector
                  ? `${detailPct}%`
                  : `calc(${100 - widths.master}% - 4px)`,
              }
            : undefined
        }
        className={cn(
          'flex min-w-0 flex-col bg-surface-base',
          isDesktop ? 'h-full min-h-0 overflow-x-hidden overflow-y-auto' : 'w-full',
        )}
      >
        {detail}
      </section>
      {inspector && (
        <>
          {isDesktop && <Splitter onPointerDown={startDrag('detail')} />}
          <aside
            style={isDesktop ? { width: `${inspectorPct}%` } : undefined}
            className={cn(
              'flex min-w-0 flex-col bg-surface-raised',
              isDesktop
                ? 'h-full min-h-0 overflow-x-hidden overflow-y-auto border-l border-border'
                : 'w-full border-t border-border',
            )}
          >
            {inspector}
          </aside>
        </>
      )}
    </div>
  );
}

function Splitter({ onPointerDown }: { onPointerDown: () => void }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      // Pointer events cover mouse + pen (and fire before the document-level
      // mousemove drag tracking attaches); the splitter stays a visual
      // separator rather than a keyboard widget.
      onPointerDown={onPointerDown}
      className="group relative h-full w-1 shrink-0 cursor-col-resize"
    >
      <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary/40 group-active:bg-primary" />
    </div>
  );
}
